import * as STD      from '@emersya/mpa-std-lib';
import * as BasketNS from './';

export class Part {
	readonly parent : BasketNS.Product | Part;
	readonly id : string;
	readonly title : string; // the name...
	readonly parts : Part[] = [];
	readonly options : BasketNS.Option[] = [];
	readonly descriptor : Part.IDescriptor;
	reference : BasketNS.Reference | null = null;
	currentOption : BasketNS.Option | null = null;
	metaData : STD.object.JSONComplexRecursive | null = null;
	computedPrice = BasketNS.Price.ofZero();
	isInitialized = false;
	ignoreOnSave = false;
	readonly pricing : BasketNS.Pricing.TPricing | null = null;

	computePrice() {
		const computedPrice = BasketNS.Price.ofZero();
		if (this.ignoreOnSave) {
			return computedPrice;
		}
		if (this.currentOption) {
			const computedOptionPrice = this.currentOption.computePrice();
			computedPrice.add(computedOptionPrice);
		}
		if (this.currentOption || (this.options.length === 0)) {
			//Take into account sub parts only if part has a current option or no options
			for (const subpart of this.parts) {
				const subpartPrice = subpart.computePrice();
				computedPrice.add(subpartPrice);
			}
		}
		if (this.pricing) {
			computedPrice.add(this.pricing.resolve());
		}
		this.computedPrice = computedPrice;
		return computedPrice;
	};

	getPart(partsPath : string[]) : Part {
		const [ partId, ...remainingPath ] = [ ...partsPath ];
		const p = this.parts.find(x => x.id === partId);
		if (!p) {
			throw STD.errors.user(
				`Unknown part<${ partId }>!`,
				{
					partId,
					partsPath
				}
			);
		}
		if (remainingPath.length !== 0) {
			return p.getPart(remainingPath);
		}
		return p;
	};

	getPath() : string {
		if (this.parent instanceof Part) {
			return this.parent.getPath() + '.' + this.id;
		}
		return this.parent.id + '.' + this.id;
	};

	createOption(desc : Omit<BasketNS.Option.IDescriptor, 'part'>) {
		const option = new BasketNS.Option(Object.assign({}, desc, {
			part : this
		}));
		this.options.push(option);
		return option;
	};

	createPart(desc : Omit<Part.IDescriptor, 'parent'>) {
		const internalDescriptor = Object.assign({}, desc, {
			parent : this
		});
		if (Object.getOwnPropertyDescriptor(desc, 'title')?.get) {
			Object.defineProperty(internalDescriptor, 'title', {
				get() {
					return desc.title;
				}
			});
		}
		return new Part(internalDescriptor);
	};

	async createPartAndInitialize(desc : Omit<Part.IDescriptor, 'parent'>) {
		const part = this.createPart(desc);
		await part.initialize();
		return (part);
	};

	async remove() {
		const selfIndex = this.parent.parts.indexOf(this);
		this.parent.parts.splice(selfIndex, 1);
		const product = this.getProduct();
		await product.basket.dispatchEvent('basket-update', {
			subType : 'partRemoved',
			details : {
				part : this,
				product
			}
		});
	}

	getProduct() : BasketNS.Product {
		return (
			this.parent instanceof BasketNS.Product ? this.parent :
				this.parent.getProduct()
		);
	};

	async useOption(optionName : string, keepVolume = false) {
		const option = this.options.find(x => x.id === optionName);
		if (!option) {
			throw STD.errors.user(`Option<${ optionName }> not found`, {
				optionName,
				part : this
			});
		}
		const oldOption = this.currentOption;
		this.currentOption = option;
		if (keepVolume && oldOption) {
			//do not do this.currentOption.setVolume(oldOption.volume)
			//because it will trigger actions that will already be triggered by basket-update
			this.currentOption.volume = oldOption.volume;
		}
		const product = this.getProduct();
		await product.basket.dispatchEvent('basket-update', {
			subType : 'partOptionUsed',
			details : {
				part : this,
				product,
				optionName,
				oldOption
			}
		});
	};

	async unuseOption() {
		if (!this.currentOption) {
			return;
		}
		const oldOption = this.currentOption;
		this.currentOption = null;
		const product = this.getProduct();
		await product.basket.dispatchEvent('basket-update', {
			subType : 'partOptionUnused',
			details : {
				part : this,
				product,
				oldOption
			}
		});
	};

	serialize() : Part.ISerialized {
		return {
			name :          this.id,
			title :         this.title,
			parts :         this.parts.filter(x => !x.ignoreOnSave).map(x => x.serialize()),
			computedPrice : this.computedPrice.serialize(),
			price :         this.pricing ? this.pricing.resolve().serialize() : BasketNS.Price.ofZero().serialize(),
			pricing :       this.pricing ? this.pricing.serialize() : null,
			metaData :      this.metaData ? Object.assign({}, this.metaData) : null,
			reference :     this.reference ? this.reference.serialize() : null,
			currentOption : this.currentOption ? this.currentOption.serialize() : null
		};
	};

	async initialize() {
		if (this.isInitialized) {
			console.warn('Already initialized part!');
			return;
		}
		for (const part of this.parts) {
			await part.initialize();
		}
		if (this.descriptor.defaultOption) {
			await this.useOption(this.descriptor.defaultOption);
		}
		this.isInitialized = true;
	};

	constructor(desc : Part.IDescriptor) {
		this.descriptor = desc;
		this.id = desc.id;
		this.title = desc.title;
		if (desc.useDynamicTitleBinding) {
			Object.defineProperty(this, 'title', {
				get() {
					return desc.title;
				}
			});
		}
		this.parent = desc.parent;
		this.parent.parts.push(this);
		if (desc.reference) {
			this.reference = (desc.reference instanceof BasketNS.Reference) ? desc.reference :
				BasketNS.Reference.deserialize(desc.reference);
		}
		if (desc.metaData) {
			this.metaData = desc.metaData;
		}
		if (desc.pricing) {
			this.pricing = desc.pricing;
		}
		for (const optionDesc of desc.options) {
			this.createOption(optionDesc);
		}
		if (desc.parts) {
			for (const partDesc of desc.parts) {
				this.createPart(partDesc);
			}
		}
		if (this.pricing && this.pricing instanceof BasketNS.Pricing.BoundPricing) {
			this.pricing._host = this;
		}
	};
};
export namespace Part {
	export interface IDescriptor {
		readonly parent : BasketNS.Product | Part;
		readonly id : string;
		readonly title : string; // the name...
		readonly reference : BasketNS.Reference | null;
		readonly metaData : STD.object.JSONComplexRecursive | null;
		readonly parts : Omit<IDescriptor, 'parent'>[];
		readonly options : Omit<BasketNS.Option.IDescriptor, 'part'>[];
		readonly defaultOption? : string | null;
		readonly useDynamicTitleBinding? : boolean;
		readonly pricing? : BasketNS.Pricing.TPricing | null;
	};

	export interface ISerialized {
		readonly name : string;
		readonly title : string;
		readonly reference : BasketNS.Reference.ISerialized | null;
		readonly metaData : STD.object.JSONComplexRecursive | null;
		readonly parts : ISerialized[];
		readonly currentOption : BasketNS.Option.ISerialized | null;
		readonly price : BasketNS.Price.ISerialized; // => computed price
		readonly pricing : BasketNS.Pricing.TPricingSerialized | null;
		readonly computedPrice : BasketNS.Price.ISerialized; // => computed price
	};
}
